import React from 'react';
import { Badge, Card } from '@openedx/paragon';

type JsonObject = Record<string, unknown>;

const objectValue = (value: unknown): JsonObject => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : {}
);
const arrayValue = (value: unknown): JsonObject[] => (
  Array.isArray(value) ? value.map(objectValue) : []
);
const text = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
const number = (value: unknown): number => (typeof value === 'number' ? value : 0);
const stringValues = (value: unknown): string[] => (
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
);

const Metric: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <span className="ai-assisted-course-authoring-tab__metric"><strong>{value}</strong> {label}</span>
);

const sourceLabel = (source: string): string => {
  try {
    return new URL(source).hostname.replace(/^www\./, '');
  } catch {
    return source.length > 24 ? `${source.slice(0, 12)}...` : source;
  }
};

const isDoi = (source: string): boolean => /^10\.\d{4,9}\/\S+$/i.test(source);

const sourceHref = (source: string): string | null => {
  if (/^https?:\/\//i.test(source)) {
    return source;
  }
  return isDoi(source) ? `https://doi.org/${source}` : null;
};

const SourceCitations: React.FC<{ sources: unknown }> = ({ sources }) => {
  const citations = stringValues(sources);
  if (!citations.length) { return null; }
  return (
    <div className="ai-assisted-course-authoring-tab__source-citations">
      <strong>Sources</strong>
      <ul>
        {citations.map(source => {
          const href = sourceHref(source);
          return href ? (
            <li key={source}>
              <a href={href} target="_blank" rel="noopener noreferrer">{href}</a>
            </li>
          ) : <li key={source} title={`Internal source: ${source}`}>{sourceLabel(source)}</li>;
        })}
      </ul>
    </div>
  );
};

const UnitSummary: React.FC<{ unit: JsonObject }> = ({ unit }) => {
  const type = text(unit.type, 'content');
  const template = text(unit.template_used).replace('content_', '').replace(/_/g, ' ');
  return (
    <li className="ai-assisted-course-authoring-tab__course-unit">
      <div className="ai-assisted-course-authoring-tab__course-unit-heading">
        <strong>{text(unit.stub_title, 'Unit')}</strong>
        {number(unit.word_count) > 0 && <Badge variant="light">{number(unit.word_count)} words</Badge>}
        {template && <Badge variant="light">{template}</Badge>}
        <Badge variant="light">{unit.rag_grounded ? 'RAG' : 'LLM'}</Badge>
        {type !== 'content' && <Badge variant="light">{type.replace(/_/g, ' ')}</Badge>}
      </div>
      {text(unit.learning_objective) && <p>{text(unit.learning_objective)}</p>}
      <SourceCitations sources={unit.sources_cited} />
    </li>
  );
};

export const KnowledgeMapPreview: React.FC<{ result: unknown }> = ({ result }) => {
  const output = objectValue(result);
  const map = objectValue(output.knowledge_map || result);
  const skills = arrayValue(map.skills);
  return (
    <Card className="ai-assisted-course-authoring-tab__preview" data-testid="knowledge-map-preview">
      <Card.Body className="ai-assisted-course-authoring-tab__card-body">
        <h4>Knowledge map ready for review</h4>
        <div className="ai-assisted-course-authoring-tab__metrics">
          <Metric label="skills" value={skills.length} />
          <Metric label="concepts" value={number(map.total_concept_count)} />
          <Metric label="sources" value={number(map.source_count)} />
        </div>
        {skills.map((skill, index) => {
          const concepts = arrayValue(skill.concepts);
          const name = text(skill.skill, `Skill ${index + 1}`);
          return (
            <details key={name} className="ai-assisted-course-authoring-tab__disclosure">
              <summary><strong>{name}</strong><Badge variant="light">{concepts.length} concepts</Badge></summary>
              {text(skill.definition) && <p>{text(skill.definition)}</p>}
              <ul>
                {concepts.map((concept, conceptIndex) => (
                  <li key={text(concept.name, `Concept ${conceptIndex + 1}`)}>
                    <strong>{text(concept.name, 'Concept')}</strong>
                    {text(concept.description) && <span> — {text(concept.description)}</span>}
                    {text(concept.why_it_matters) && <p>{text(concept.why_it_matters)}</p>}
                  </li>
                ))}
              </ul>
            </details>
          );
        })}
      </Card.Body>
    </Card>
  );
};

export const OutlinePreview: React.FC<{ result: unknown }> = ({ result }) => {
  const output = objectValue(result);
  const outline = objectValue(output.approved_outline || result);
  const sections = arrayValue(outline.sections);
  return (
    <Card className="ai-assisted-course-authoring-tab__preview" data-testid="outline-preview">
      <Card.Body className="ai-assisted-course-authoring-tab__card-body">
        <h4>Course outline ready for review</h4>
        <div className="ai-assisted-course-authoring-tab__metrics">
          <Metric label="sections" value={sections.length} />
          <Metric label="units" value={number(outline.unit_count)} />
          <Metric label="minutes" value={number(outline.duration_estimate_minutes)} />
        </div>
        {sections.map((section, sectionIndex) => {
          const subsections = arrayValue(section.subsections);
          const title = text(section.title, `Section ${sectionIndex + 1}`);
          return (
            <details key={title} className="ai-assisted-course-authoring-tab__disclosure">
              <summary><strong>{title}</strong>{text(section.skill) && <Badge variant="light">{text(section.skill)}</Badge>}</summary>
              {subsections.map((subsection, subsectionIndex) => {
                const units = arrayValue(subsection.unit_stubs || subsection.units);
                return (
                  <div className="ai-assisted-course-authoring-tab__outline-subsection" key={text(subsection.title, `Topic ${subsectionIndex + 1}`)}>
                    <h5>{text(subsection.title, `Topic ${subsectionIndex + 1}`)}</h5>
                    <ul>{units.filter(unit => text(unit.type, 'content') !== 'checkin').map((unit, unitIndex) => <li key={text(unit.title, `Unit ${unitIndex + 1}`)}>{text(unit.title, 'Unit')}</li>)}</ul>
                  </div>
                );
              })}
            </details>
          );
        })}
      </Card.Body>
    </Card>
  );
};

export const CourseContentPreview: React.FC<{ result: unknown; outlineResult?: unknown }> = ({
  result,
  outlineResult,
}) => {
  const output = objectValue(result);
  const course = objectValue(output.course_content || result);
  const sections = arrayValue(course.sections);
  const confidence = objectValue(course.confidence_report);
  const outlineOutput = objectValue(outlineResult);
  const outline = objectValue(outlineOutput.approved_outline || outlineResult);
  const outlineSections = arrayValue(outline.sections);
  return (
    <Card className="ai-assisted-course-authoring-tab__preview" data-testid="course-content-preview">
      <Card.Body className="ai-assisted-course-authoring-tab__card-body">
        <h4>Course content generated</h4>
        <div className="ai-assisted-course-authoring-tab__metrics">
          <Metric label="sections" value={sections.length} />
          <Metric label="units" value={number(confidence.unit_count)} />
          <Metric label="words" value={number(confidence.total_word_count).toLocaleString()} />
          <Metric label="minutes" value={number(confidence.estimated_learner_minutes)} />
        </div>
        {Object.keys(confidence).length > 0 && (
          <section className="ai-assisted-course-authoring-tab__confidence-report" aria-labelledby="course-confidence-heading">
            <h5 id="course-confidence-heading">Confidence report</h5>
            <div className="ai-assisted-course-authoring-tab__metrics">
              <Metric label="skill coverage" value={`${Math.round(number(confidence.skill_coverage_score) * 100)}%`} />
              <Metric label="source citations" value={`${Math.round(number(confidence.source_citation_ratio) * 100)}%`} />
            </div>
            {stringValues(confidence.flagged_sections).length > 0 && <p>Flagged sections: {stringValues(confidence.flagged_sections).join(', ')}</p>}
          </section>
        )}
        {sections.map((section, sectionIndex) => {
          const units = arrayValue(section.units).filter(unit => text(unit.type) !== 'checkin');
          const contentUnits = units.filter(unit => text(unit.type) === 'content');
          const introUnit = units.find(unit => text(unit.type) === 'section_intro');
          const summaryUnit = units.find(unit => text(unit.type) === 'section_summary');
          const sectionTitle = text(section.section_title, text(section.skill, `Section ${sectionIndex + 1}`));
          const outlineSection = outlineSections.find(candidate => (
            text(candidate.skill) === text(section.skill) || text(candidate.title) === sectionTitle
          ));
          const subsections = arrayValue(outlineSection?.subsections);
          const mappedTitles = new Set<string>();
          return (
            <details key={sectionTitle} className="ai-assisted-course-authoring-tab__disclosure">
              <summary>
                <strong>{sectionTitle}</strong>
                <Badge variant="light">{contentUnits.length} units</Badge>
                {text(section.skill) && <Badge variant="light">{text(section.skill)}</Badge>}
              </summary>
              <div className="ai-assisted-course-authoring-tab__course-section-content">
                {introUnit && (
                  <ul className="ai-assisted-course-authoring-tab__course-unit-list">
                    <UnitSummary unit={introUnit} />
                  </ul>
                )}
                {subsections.map((subsection, subsectionIndex) => {
                  const stubTitles = arrayValue(subsection.unit_stubs || subsection.units)
                    .filter(stub => text(stub.type, 'content') === 'content')
                    .map(stub => text(stub.title))
                    .filter(Boolean);
                  stubTitles.forEach(title => mappedTitles.add(title));
                  const subsectionUnits = stubTitles
                    .map(title => contentUnits.find(unit => text(unit.stub_title) === title))
                    .filter((unit): unit is JsonObject => Boolean(unit));
                  return (
                    <Card className="ai-assisted-course-authoring-tab__course-subsection" key={text(subsection.title, `Topic ${subsectionIndex + 1}`)}>
                      <Card.Body className="ai-assisted-course-authoring-tab__card-body">
                        <details>
                          <summary><strong>{text(subsection.title, `Topic ${subsectionIndex + 1}`)}</strong><Badge variant="light">{subsectionUnits.length} units</Badge></summary>
                          <ul className="ai-assisted-course-authoring-tab__course-unit-list">
                            {subsectionUnits.map(unit => (
                              <UnitSummary key={text(unit.stub_title, 'Unit')} unit={unit} />
                            ))}
                          </ul>
                        </details>
                      </Card.Body>
                    </Card>
                  );
                })}
                {contentUnits.filter(unit => !mappedTitles.has(text(unit.stub_title))).length > 0 && (
                  <ul className="ai-assisted-course-authoring-tab__course-unit-list">
                    {contentUnits.filter(unit => !mappedTitles.has(text(unit.stub_title))).map(unit => (
                      <UnitSummary key={text(unit.stub_title, 'Unit')} unit={unit} />
                    ))}
                  </ul>
                )}
                {summaryUnit && (
                  <ul className="ai-assisted-course-authoring-tab__course-unit-list">
                    <UnitSummary unit={summaryUnit} />
                  </ul>
                )}
              </div>
            </details>
          );
        })}
      </Card.Body>
    </Card>
  );
};
